import {
  draftRoundTypes,
  loadStepFor,
  recognizeRule,
  type Draft,
  type Recognition,
  type RoundType,
  type SlotDraft,
  type SlotRecognizer,
  type UnitsPreference,
} from "@heraion/shared";

// The rule of a slot, recognized once per slot object, per round types and
// per load grain (#336 slice 5). Reading a rule is not free on Hermes, and
// the Draft is immutable: an edit makes a new slot object, so the old entry
// simply stops being asked for and the WeakMap lets it go.
const CACHE = new WeakMap<SlotDraft, Map<string, Recognition>>();

/** `loadQuantum`: the grain a scaled load rounds to, loadStepFor(cellUnit). */
export const ruleOfSlot = (slot: SlotDraft, types: readonly RoundType[], loadQuantum?: number): Recognition => {
  const key = `${types.join()}|${loadQuantum ?? ""}`;
  let byKey = CACHE.get(slot);
  if (!byKey) {
    byKey = new Map();
    CACHE.set(slot, byKey);
  }
  const hit = byKey.get(key);
  if (hit) return hit;
  const rec = recognizeRule(slot, types, loadQuantum === undefined ? {} : { loadQuantum });
  byKey.set(key, rec);
  return rec;
};

/** The rule of slot `slotIdx` of day `sIdx`, or «valori a mano». */
export const ruleAt = (draft: Draft, sIdx: number, slotIdx: number, loadQuantum?: number): Recognition => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  return slot ? ruleOfSlot(slot, draftRoundTypes(draft), loadQuantum) : { kind: "manual" };
};

export type RuleOptions = { loadQuantum: number; recognize: SlotRecognizer };

const OPTIONS = new Map<UnitsPreference, RuleOptions>();

/** What every rule call of the editor passes: the load grain of the cells'
 *  unit (5 lb, not 2.5), and this cache as the recognizer. One object per
 *  unit, so it can sit in a memo's deps. */
export const ruleOptions = (unit: UnitsPreference = "kg"): RuleOptions => {
  let o = OPTIONS.get(unit);
  if (!o) {
    const loadQuantum = loadStepFor(unit);
    o = { loadQuantum, recognize: (slot, types) => ruleOfSlot(slot, types, loadQuantum) };
    OPTIONS.set(unit, o);
  }
  return o;
};
